/**
 * Protected Route Component
 * Redirects unauthenticated users to login
 *
 * Per AERO-EDC spec:
 * - 5 roles: admin, site-rc, site, analyst, cec
 * - Admin can access all pages
 * - Each role has specific access
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'site-rc' | 'site' | 'analyst' | 'cec';
}

// Role access rules - per AERO-EDC spec
const roleAccess: Record<string, string[]> = {
  admin: ['admin'],                          // Admin-only pages
  'site-rc': ['site-rc', 'site', 'admin'],   // Site pages (admin can also access)
  site: ['site', 'site-rc', 'admin'],        // Site pages
  analyst: ['analyst', 'admin'],             // Analyst pages (admin can also access)
  cec: ['cec', 'admin'],                    // CEC pages (admin can also access)
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-opci-navy-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force password change on first login / after admin reset
  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Role-based access check
  if (requiredRole && user) {
    const allowedRoles = roleAccess[requiredRole] || [requiredRole];

    if (!allowedRoles.includes(user.role)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800">Access Denied</h1>
            <p className="mt-2 text-gray-600">
              You don't have permission to access this page.
            </p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
